import React, { Component } from "react";
import Grid from "@material-ui/core/Grid/Grid";
import withStyles from "@material-ui/core/styles/withStyles";
import Select from "@material-ui/core/Select/Select";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Input from "@material-ui/core/Input/Input";
import Divider from "@material-ui/core/Divider/Divider";
import Button from "@material-ui/core/Button/Button";
import * as R from "ramda";
import Tips from "./Tips";
import Script from "./Script";
import Visualizer from "./Visualizer";
import { saveRecording } from "../../lib/RecorderService";
import { upCase } from "../../lib/utils";
import { Tooltip } from "@material-ui/core";

const styles = {
  select: {
    marginTop: 32,
  },
  divider: {
    marginTop: 15,
    marginBottom: 22,
  },
  recordingControls: {
    marginTop: 20,
    marginBottom: 20,
  },
  heading: {
    marginBottom: 5,
    marginTop: 5,
    textAlign: "center",
  },
};

const notNil = R.complement(R.isNil);

const formatOption = R.pipe(
  R.cond([
    [R.equals("p_c"), R.always("p&c")],
    [notNil, R.identity],
  ]),
  R.split("_"),
  R.map(upCase),
  R.join(" ")
);

const findRecording = (group, type, compliant, recordingTypes) =>
  R.find(
    R.allPass([
      R.pipe(R.prop("group"), R.equals(group)),
      R.pipe(
        R.prop("recording_type"),
        R.equals(R.replace(" - Modified", "", type))
      ),
      R.pipe(
        R.propOr(false, "compliant"),
        R.equals(R.endsWith("Modified", type))
      ),
    ])
  )(recordingTypes);

class Recorder extends Component {
  constructor(props) {
    super(props);
    const { recordingTypes } = this.props;
    this.state = {
      group: recordingTypes[0]["group"],
      type: `${recordingTypes[0]["recording_type"]} - Modified`,
      compliant: R.propOr(false, "compliant", recordingTypes[0]),
      recording: null,
      recordingUrl: null,
      isRecording: false,
      isUploading: false,
      isPlayedBack: false,
      uploadMessage: null,
    };
  }

  startRecording = () =>
    this.setState({
      isRecording: true,
      uploadMessage: null,
      isPlayedBack: false,
    });

  stopRecording = () =>
    this.setState({ isRecording: false, uploadMessage: null });

  clearRecording = () =>
    this.setState({
      isRecording: false,
      recording: null,
      recordingUrl: null,
      uploadMessage: null,
    });

  onRecordingComplete = (recording) =>
    this.setState({ recordingUrl: recording.blobURL, recording });

  canUpload = () => {
    const { recording, group, type } = this.state;
    return recording && group && type;
  };

  updateGroup = (e) => {
    e.preventDefault();
    const newGroupValue = e.target.value;
    this.setState({
      isRecording: false,
      recording: null,
      recordingUrl: null,
      uploadMessage: null,
      group: newGroupValue,
      type: R.head(this.getRecordingTypesForGroup(newGroupValue)),
    });
  };

  updateRecordingType = (e) => {
    e.preventDefault();
    this.setState({
      isRecording: false,
      recording: null,
      recordingUrl: null,
      uploadMessage: null,
      type: e.target.value,
    });
  };

  getRecordingTypesForGroup = (group) =>
    R.pipe(
      R.filter(R.pipe(R.prop("group"), R.equals(group))),
      R.map(
        R.ifElse(
          R.propEq("compliant", true),
          R.pipe(R.prop("recording_type"), R.concat(R.__, " - Modified")),
          R.prop("recording_type")
        )
      )
    )(this.props.recordingTypes);

  getGroups = () =>
    R.pipe(R.map(R.prop("group")), R.uniq)(this.props.recordingTypes);

  upload = async (e) => {
    e.preventDefault();
    this.setState({ uploading: true });
    const { type, group, recording } = this.state;
    const { fetchRecordings } = this.props;
    const recordingData = new FormData();

    recordingData.append("recording_type", R.replace(" - Modified", "", type));
    recordingData.append("group", group);
    recordingData.append("compliant", R.endsWith("Modified", type));
    recordingData.append("data", recording.blob);

    try {
      await saveRecording(recordingData);
      this.setState({
        uploading: false,
        uploadMessage: "Successfully uploaded your intro",
      });
      fetchRecordings();
    } catch (e) {
      this.setState({
        uploading: false,
        uploadMessage:
          "There was an error uploading your intro. Please try again later.",
      });
    }
  };

  renderOption = (entry) => (
    <MenuItem value={entry} key={entry}>
      {formatOption(entry)}
    </MenuItem>
  );

  renderRecordingButtons = () => {
    const { classes } = this.props;
    return (
      <Grid container spacing={16} className={classes.recordingControls}>
        <Grid item xs={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.startRecording}
          >
            Start Recording
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            variant="contained"
            color="secondary"
            onClick={this.stopRecording}
          >
            Stop Recording
          </Button>
        </Grid>
        <Grid item xs={3} onClick={this.clearRecording}>
          <Button variant="contained">Clear Recording</Button>
        </Grid>
      </Grid>
    );
  };

  renderRecordingSelection = () => {
    const { classes } = this.props;
    const { group, type } = this.state;
    return (
      <Grid container direction="row">
        <Grid container direction="row">
          <Grid item xs={12}>
            <h2 className={classes.heading}>Select a recording type</h2>
          </Grid>
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={3}>
          <Select
            value={group}
            input={<Input name="Insurance Line" />}
            className={classes.select}
            onChange={this.updateGroup}
          >
            {R.map(this.renderOption, this.getGroups())}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <Select
            value={type}
            input={<Input name="Type" />}
            className={classes.select}
            onChange={this.updateRecordingType}
          >
            {R.map(this.renderOption, this.getRecordingTypesForGroup(group))}
          </Select>
        </Grid>
        <Grid item xs={3} />
      </Grid>
    );
  };

  render() {
    const { classes, recordingTypes } = this.props;
    const {
      group,
      type,
      compliant,
      uploading,
      uploadMessage,
      recordingUrl,
      isRecording,
      isPlayedBack,
    } = this.state;
    const uploadReady = this.canUpload();
    return (
      <Grid>
        <Grid container spacing={16} style={{ marginBottom: "24px" }}>
          <Grid item xs={3} />
          <Grid item xs={6}>
            {this.renderRecordingSelection()}
            <Divider className={classes.divider} />
            <Script
              selectedRecording={findRecording(
                group,
                type,
                compliant,
                recordingTypes
              )}
            />
            <Divider className={classes.divider} />
            <Tips />
            <Divider className={classes.divider} />
            <Visualizer
              onRecordingComplete={this.onRecordingComplete}
              isRecording={isRecording}
              recordingUrl={recordingUrl}
              onPlaybackEnded={() => this.setState({ isPlayedBack: true })}
            />
            <Divider className={classes.divider} />
            {this.renderRecordingButtons()}
            <Grid container>
              <Grid item xs={3}>
                <Tooltip
                  title={
                    uploadReady && !isPlayedBack
                      ? "Playback Required: Please listen to your recording before uploading."
                      : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!uploadReady || uploading || !isPlayedBack}
                      onClick={this.upload}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={6}>
                {uploadMessage}
              </Grid>
              <Grid item xs={3} />
            </Grid>
          </Grid>
          <Grid item xs={3} />
        </Grid>
      </Grid>
    );
  }
}

export default withStyles(styles)(Recorder);
